import { FieldProps } from 'formik'
import React from 'react'
import { Form } from 'react-bulma-components'
import { isNumber } from 'lodash'
import { hoursAsNumberToTimeInputValue, timeInputValueToHoursAsNumber } from '../../../utils/date'
import FormField, { FormFieldProps } from './form-field'

export interface TimePickerProps
  extends Pick<FormFieldProps, 'label' | 'error' | 'required' | 'help' | 'htmlFor' | 'dataTest'> {
  /** Value in "HH:mm" format. Conversion (decimal hours, Date, etc.) is the parent's responsibility. */
  value: string
  /** Called with the raw "HH:mm" string on change. */
  onChange: (value: string) => void
  onBlur?: () => void
  disabled?: boolean
  size?: 'small' | 'normal'
  name?: string
  /** If set, adds data-tip and data-for on the input for ReactTooltip. */
  dataFor?: string
  /** Applied to the underlying Form.Input. */
  inputStyle?: React.CSSProperties
  /** Passed to Form.Input (Bulma). */
  textWeight?: 'bold' | 'normal' | 'light' | 'semibold'
  /** Passed to Form.Input (Bulma). */
  textColor?: 'grey' | 'grey-light' | 'primary' | 'danger' | 'warning' | 'success' | 'info' | 'link'
}

/**
 * Native time input (type="time") with label and error display.
 * Value is always a string in "HH:mm" format; the parent must convert to/from
 * decimal hours, Date, or other formats.
 */
const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  onBlur,
  label,
  error,
  required,
  help,
  htmlFor,
  disabled = false,
  size = 'small',
  name,
  dataTest,
  dataFor,
  inputStyle,
  textWeight,
  textColor,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  return (
    <FormField
      label={label}
      error={error}
      required={required}
      help={help}
      htmlFor={htmlFor}
      dataTest={dataTest}
    >
      <Form.Input
        type="time"
        size={size}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        name={name}
        style={inputStyle}
        textWeight={textWeight}
        textColor={textColor}
        {...(dataTest ? { 'data-test': dataTest } : {})}
        {...(dataFor ? { 'data-tip': true, 'data-for': dataFor } : {})}
      />
    </FormField>
  )
}

export default TimePicker

/** Props for the Formik-connected time picker (field value = decimal hours 0–24). */
export interface TimePickerFieldProps extends FormFieldProps, FieldProps {}

/**
 * Formik Field component for time-of-day stored as decimal hours (0–24).
 * Use with <Field name="..." component={TimePickerField} label="..." />.
 * Converts between form value (number) and "HH:mm" internally.
 */
export const TimePickerField: React.FC<TimePickerFieldProps> = props => {
  const { field, form, label, help, required } = props
  const value = isNumber(field.value) ? hoursAsNumberToTimeInputValue(field.value) : ''

  return (
    <TimePicker
      label={label}
      value={value}
      onChange={value => {
        const hours = timeInputValueToHoursAsNumber(value)
        if (hours !== undefined) {
          form.setFieldValue(field.name, hours)
          form.setFieldTouched(field.name, true, false)
        }
      }}
      onBlur={() => form.setFieldTouched(field.name, true, false)}
      error={form.errors[field.name] as string | string[] | undefined}
      required={required}
      help={help}
    />
  )
}
